export type DetectionCategory =
  | "ip"
  | "url"
  | "email"
  | "token"
  | "secret"
  | "resident-id"
  | "phone"
  | "face"
  | "logo"
  | "manual";

export type DetectionSource =
  | "local-ocr"
  | "local-face"
  | "local-llm"
  | "local-vlm"
  | "gemini"
  | "manual"
  | "memory";

export type BoxAction = "mask" | "ignore";
export type MaskMode = "opaque" | "pixelate" | "blur";
export type TextMaskLevel = "full" | "partial";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DetectionBox {
  id: string;
  category: DetectionCategory;
  bbox: Rect;
  confidence: number;
  source: DetectionSource;
  text?: string;
  reason?: string;
  evidenceText?: string;
  requiresReview?: boolean;
  action: BoxAction;
}

export interface PresetConfig {
  id: string;
  label: string;
  categories: readonly DetectionCategory[];
}

export interface DetectorProvider {
  id: string;
  detect(input: ImageBitmap, preset: PresetConfig): Promise<DetectionBox[]>;
}

export interface OcrWord {
  text: string;
  bbox: Rect;
  confidence?: number;
}

export interface OcrLine {
  text: string;
  bbox: Rect;
  words?: OcrWord[];
}

export type LocalAiProfile = "fast" | "precise";
export type LocalAiModelKind = "text" | "vision";
export type LocalAiRuntime = "tauri-sidecar" | "docker" | "ollama-compatible" | "llama.cpp" | "unknown";
export type LocalAiReviewDecision = "keep" | "drop" | "change";

export interface LocalAiHealth {
  ok: boolean;
  runtime: LocalAiRuntime;
  version?: string;
  modelsReady?: boolean;
  visionReady?: boolean;
  vlmReady?: boolean;
  profiles?: LocalAiProfile[];
  message?: string;
}

export interface LocalAiModel {
  id: string;
  kind: LocalAiModelKind;
  profile?: LocalAiProfile;
  displayName?: string;
}

export interface LocalAiImageInfo {
  width: number;
  height: number;
  dataUrl?: string;
}

export interface LocalAiCrop {
  id: string;
  bbox: Rect;
  dataUrl: string;
  reason?: string;
}

export interface LocalAiContextInput {
  image: Pick<LocalAiImageInfo, "width" | "height">;
  ocrLines: OcrLine[];
  existingBoxes: DetectionBox[];
  preset: PresetConfig;
  profile: LocalAiProfile;
}

export interface LocalAiVisionInput {
  image: LocalAiImageInfo;
  crops: LocalAiCrop[];
  existingBoxes: DetectionBox[];
  preset: PresetConfig;
  profile: LocalAiProfile;
}

export interface LocalAiVlmTile {
  id: string;
  bbox: Rect;
  dataUrl: string;
}

export interface LocalAiVlmMainInput {
  image: LocalAiImageInfo;
  tiles: LocalAiVlmTile[];
  preset: PresetConfig;
  profile: LocalAiProfile;
}

export interface LocalAiVlmDiagnostics {
  model?: string;
  httpStatus?: number;
  schemaMode?: boolean;
  rawResponseExcerpt?: string;
  acceptedCount: number;
  rejectedCount: number;
  invalidCategoryCount: number;
  placeholderRejectedCount: number;
  parseErrorCount: number;
}

export interface FaceDetectionDiagnostics {
  engine: "native" | "mediapipe" | "human" | "unavailable";
  faceCount: number;
  errorCode?: string;
  errorMessage?: string;
  elapsedMs?: number;
}

export interface LocalAiVlmMainResponse {
  boxes: DetectionBox[];
  diagnostics?: LocalAiVlmDiagnostics;
}

export interface LocalAiReviewInput {
  image: Pick<LocalAiImageInfo, "width" | "height">;
  ocrLines: OcrLine[];
  candidateBoxes: DetectionBox[];
  preset: PresetConfig;
  profile: LocalAiProfile;
}

export interface LocalAiReview {
  boxId: string;
  decision: LocalAiReviewDecision;
  category?: DetectionCategory;
  confidence?: number;
  reason?: string;
  requiresReview?: boolean;
}

export interface LocalAiProvider {
  health(): Promise<LocalAiHealth>;
  models(): Promise<LocalAiModel[]>;
  detectContext(input: LocalAiContextInput): Promise<DetectionBox[]>;
  detectVision(input: LocalAiVisionInput): Promise<DetectionBox[]>;
  detectVlmMain(input: LocalAiVlmMainInput): Promise<LocalAiVlmMainResponse>;
  reviewBoxes(input: LocalAiReviewInput): Promise<LocalAiReview[]>;
}

export interface MemoryProfile {
  ignoredTexts?: string[];
}

export interface MaskSettings {
  mode: MaskMode;
  textLevel: TextMaskLevel;
  pixelSize: number;
  blurRadius: number;
  fillColor: string;
}

export const DEFAULT_MASK_SETTINGS: MaskSettings = {
  mode: "pixelate",
  textLevel: "full",
  pixelSize: 12,
  blurRadius: 10,
  fillColor: "#0b1020"
};

export const TEXT_CATEGORIES = new Set<DetectionCategory>([
  "ip",
  "url",
  "email",
  "token",
  "secret",
  "resident-id",
  "phone"
]);

export function getDefaultMaskMode(category: DetectionCategory): MaskMode {
  if (category === "token" || category === "secret" || category === "resident-id") {
    return "opaque";
  }
  return "pixelate";
}

export function clampBoxToImage(box: Rect, imageWidth: number, imageHeight: number): Rect {
  const x = clamp(box.x, 0, imageWidth);
  const y = clamp(box.y, 0, imageHeight);
  const right = clamp(box.x + box.width, 0, imageWidth);
  const bottom = clamp(box.y + box.height, 0, imageHeight);
  return {
    x,
    y,
    width: Math.max(0, right - x),
    height: Math.max(0, bottom - y)
  };
}

export function dedupeBoxes(boxes: DetectionBox[], overlapThreshold = 0.45): DetectionBox[] {
  const sorted = [...boxes].sort((a, b) => b.confidence - a.confidence);
  const kept: DetectionBox[] = [];

  for (const box of sorted) {
    const duplicate = kept.some(
      (candidate) =>
        candidate.category === box.category && iou(candidate.bbox, box.bbox) >= overlapThreshold
    );
    if (!duplicate) {
      kept.push(box);
    }
  }

  return kept.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
}

export function sanitizeDownloadName(originalName: string, extension: "png" | "jpg" | "jpeg"): string {
  const withoutExt = originalName.replace(/\.[^.]+$/, "");
  const safeBase =
    withoutExt
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9가-힣]+/gi, "-")
      .replace(/^-+|-+$/g, "") || "image";
  return `${safeBase}-redacted.${extension === "jpeg" ? "jpg" : extension}`;
}

export function redactTextPreview(
  text: string,
  category: DetectionCategory,
  level: TextMaskLevel
): string {
  if (level === "full") {
    return maskVisibleChars(text);
  }

  switch (category) {
    case "resident-id":
      return text.replace(/^(\d{6})([-\s]?)(\d{7})$/, (_match, birth, sep) => {
        return `${birth}${sep || "-"}*******`;
      });
    case "ip":
      return text.replace(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.)\d{1,3}$/, "$1xxx");
    case "phone":
      return text.replace(/^(\d{2,3}[-\s]?\d{3,4}[-\s]?)(\d{4})$/, "$1****");
    case "email": {
      const at = text.indexOf("@");
      if (at <= 1) return maskVisibleChars(text);
      return `${text.slice(0, 1)}${"*".repeat(Math.max(1, at - 1))}${text.slice(at)}`;
    }
    case "url":
      return text.replace(/^(https?:\/\/[^/?#]+)(.*)$/i, "$1/...");
    case "token":
      if (text.length <= 12) return maskVisibleChars(text);
      return `${text.slice(0, 6)}${"*".repeat(text.length - 10)}${text.slice(-4)}`;
    default:
      return maskVisibleChars(text);
  }
}

export function buildTextMaskSegments(box: DetectionBox, level: TextMaskLevel): Rect[] {
  if (!box.text || level === "full") {
    return [box.bbox];
  }

  const preview = redactTextPreview(box.text, box.category, level);
  if (preview.length !== box.text.length) {
    return [box.bbox];
  }

  const maskedRuns: Rect[] = [];
  let runStart = -1;
  for (let index = 0; index <= preview.length; index += 1) {
    const char = preview[index];
    const isMasked = char === "*" || char === "x" || char === "X";
    if (isMasked && runStart === -1) {
      runStart = index;
    }
    if ((!isMasked || index === preview.length) && runStart !== -1) {
      maskedRuns.push(textRangeToRect(box.bbox, preview.length, runStart, index));
      runStart = -1;
    }
  }

  return maskedRuns.length > 0 ? maskedRuns : [box.bbox];
}

function textRangeToRect(box: Rect, textLength: number, start: number, end: number): Rect {
  const safeLength = Math.max(1, textLength);
  const x = box.x + box.width * (start / safeLength);
  const width = box.width * ((end - start) / safeLength);
  return { x, y: box.y, width, height: box.height };
}

function maskVisibleChars(text: string): string {
  return text.replace(/[^\s-]/g, "*");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function iou(a: Rect, b: Rect): number {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  const intersection = Math.max(0, right - left) * Math.max(0, bottom - top);
  const union = area(a) + area(b) - intersection;
  return union <= 0 ? 0 : intersection / union;
}

function area(box: Rect): number {
  return Math.max(0, box.width) * Math.max(0, box.height);
}
